import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAStrip from '../../components/feature/CTAStrip';
import MeetTheTeamSection from './components/MeetTheTeamSection';
import { useNavigate } from 'react-router-dom';

export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-thl-dark">
      <Navbar />
      <div className="page-header pt-28 pb-16 px-6">
        <div className="page-header-grid" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-3 text-xs text-white/40">
            <button onClick={()=>navigate('/')} className="hover:text-white cursor-pointer">Home</button>
            <span>/</span><span className="text-white/60">About</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">People. Service. Technology.</h1>
          <p className="text-white/50 max-w-xl">Building the logistics infrastructure that enables African businesses to grow.</p>
        </div>
      </div>

      {/* Story */}
      <section className="py-20 px-6" style={{background:'#0f0f18'}}>
        <div className="max-w-7xl mx-auto">

          {/* Born from a Market Gap */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="eyebrow">Our Story</p>
              <h2 className="section-heading mb-6">Born from a Market Gap</h2>
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                In Nigeria, vision is rarely the problem—execution is. We observed a recurring challenge: ambitious businesses were not failing due to a lack of quality or drive, but because of weak logistics foundations. <strong className="text-white">Storage</strong> was inaccessible, <strong className="text-white">fulfillment</strong> was inconsistent, and the high-level technology required to manage operations was traditionally reserved for large-scale enterprises.
              </p>
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                The Hub Terminus Limited (THL) was founded to change that. Built on our core philosophy of <strong className="text-white">People, Service, and Technology</strong>, we have created a fulfillment ecosystem that levels the playing field.
              </p>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                Our goal is simple: to make world-class logistics infrastructure accessible to businesses of every size.
              </p>
              <div className="border-l-2 border-thl-red pl-5">
                <p className="text-white/80 text-sm italic leading-relaxed">
                  &ldquo;We are more than a logistics provider — we are a partner in growth.&rdquo;
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[
                {v:'2022', l:'Year Founded', ico:'ri-calendar-line'},
                {v:'100k+', l:'Sq Ft Managed', ico:'ri-building-2-line'},
                {v:'Lagos', l:'Headquarters', ico:'ri-map-pin-2-line'},
                {v:'Global', l:'Clients Served', ico:'ri-group-line'},
              ].map(s => (
                <div key={s.l} className="bg-thl-dark3 border border-thl-border rounded-xl p-6 flex flex-col items-center text-center hover:border-thl-red/30 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-thl-red/10 border border-thl-red/20 mb-4">
                    <i className={`${s.ico} text-thl-red text-lg`} />
                  </div>
                  <div className="text-thl-red font-extrabold text-3xl mb-1">{s.v}</div>
                  <div className="text-white/40 text-xs">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* The THL Advantage */}
          <div className="mb-20">
            <p className="eyebrow">The THL Advantage</p>
            <h2 className="section-heading mb-3">We Don&apos;t Just Move Boxes</h2>
            <p className="text-white/50 text-sm mb-10 max-w-2xl">We build ecosystems. Integrated solutions that simplify storage, optimize inventory, and accelerate distribution.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  ico: 'ri-store-2-line',
                  t: 'Pay As You Use Storage',
                  d: 'Flexible, scalable space that breathes with your business. No more overpaying for unused square footage.'
                },
                {
                  ico: 'ri-truck-line',
                  t: 'Smart Fulfillment',
                  d: 'Seamless outsourced and in-house solutions, including professional kitting and global distribution.'
                },
                {
                  ico: 'ri-dashboard-3-line',
                  t: 'The OMS Edge',
                  d: 'Our proprietary Order Management System provides the "God view" of your operations — real-time visibility, control, and data-driven insights across every stage of your supply chain.'
                },
              ].map(a => (
                <div key={a.t} className="bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/30 transition-all group">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-thl-red/10 border border-thl-red/20 mb-5 group-hover:bg-thl-red/20 transition-all">
                    <i className={`${a.ico} text-thl-red text-xl`} />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{a.t}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{a.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote callout */}
          <div className="mb-20 rounded-xl border border-thl-red/25 px-10 py-10 text-center" style={{background:'rgba(220,38,38,0.05)'}}>
            <i className="ri-double-quotes-l text-thl-red text-4xl opacity-50 block mb-4" />
            <p className="text-white text-xl md:text-2xl font-semibold leading-relaxed max-w-3xl mx-auto">
              We level the playing field so a local MSME can operate with the logistical precision of a global giant.
            </p>
          </div>

          {/* Who We Fuel */}
          <div className="mb-20">
            <p className="eyebrow">Who We Fuel</p>
            <h2 className="section-heading mb-3">The Businesses We Power</h2>
            <p className="text-white/50 text-sm mb-10 max-w-2xl">We provide the operational backbone for diverse sectors, united by a common need: a logistics partner invested in their growth.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {ico:'ri-heart-pulse-line', sector:'Pharmaceuticals', help:'Precision handling and secure logistics for sensitive medical supply chains.'},
                {ico:'ri-sun-line', sector:'Renewable Energy', help:'Rugged, reliable distribution systems for solar and green energy infrastructure.'},
                {ico:'ri-shopping-bag-3-line', sector:'Retail & E-Commerce', help:'Agile fulfillment that handles seasonal demand surges without the stress.'},
                {ico:'ri-store-line', sector:'MSMEs', help:'The enterprise-grade infrastructure needed to scale from "garage to global."'},
                {ico:'ri-building-4-line', sector:'Large Enterprises', help:'Optimizing complex supply chains through expert advisory and proprietary tech.'},
              ].map(s => (
                <div key={s.sector} className="bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/30 transition-all group flex gap-4 items-start">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-thl-red/10 border border-thl-red/20 group-hover:bg-thl-red/20 transition-all mt-0.5">
                    <i className={`${s.ico} text-thl-red text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{s.sector}</h3>
                    <p className="text-white/50 text-xs leading-relaxed">{s.help}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Your Partner in Growth */}
          <div className="rounded-xl border border-thl-border px-10 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8" style={{background:'#0c0c14'}}>
            <div className="max-w-2xl">
              <p className="eyebrow">Your Partner in Growth</p>
              <h2 className="section-heading mb-4">Built Around Your Needs</h2>
              <p className="text-white/60 text-sm leading-relaxed">
                Whether you are managing seasonal fluctuations, supporting a market expansion, or building a more efficient operational system, we deliver solutions that are not just reliable; they are tailored to you.
              </p>
            </div>
            <div className="flex-shrink-0">
              <button onClick={() => navigate('/contact')} className="whitespace-nowrap inline-flex items-center gap-2 bg-thl-red text-white text-sm font-semibold px-7 py-3.5 rounded-lg hover:bg-red-700 transition-colors cursor-pointer">
                Ready to transform your supply chain?
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-thl-dark3 border border-thl-border rounded-xl p-8">
            <p className="eyebrow">Our Mission</p>
            <p className="text-white text-lg leading-relaxed">&ldquo;To delight our partners by continually creating moments of unforgettable/memorable service experience. We shall do this by our People, Service, Technology (PST) Philosophy.&rdquo;</p>
          </div>
          <div className="bg-thl-dark3 border border-thl-red/20 rounded-xl p-8">
            <p className="eyebrow">Our Vision</p>
            <p className="text-white text-lg leading-relaxed">&ldquo;To be the fulfillment hub of 1st choice in Africa known for Service Excellence.&rdquo;</p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-6" style={{background:'#0c0c14'}}>
        <div className="max-w-7xl mx-auto">
          <p className="eyebrow">Philosophy</p>
          <h2 className="section-heading mb-10">People. Service. Technology.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                ico:'🤝',
                t:'People',
                d:'We believe our success is intrinsically linked to the success of our people: our dedicated team and, most importantly, you, our valued client. We foster a collaborative environment where expertise meets empathy, ensuring a deep understanding of your unique business needs. By investing in our team\'s growth and well-being, we guarantee a motivated and skilled workforce ready to go the extra mile for your business.'
              },
              {
                ico:'⭐',
                t:'Service',
                d:'Every interaction is designed around your experience. We measure our success by your success, delivering responsive, reliable, and results-driven service at every touchpoint across your supply chain.'
              },
              {
                ico:'💡',
                t:'Technology',
                d:'Technology is the foundation of everything we do. We continuously invest in smarter logistics software, from our OMS to real-time tracking, ensuring you always have full visibility and control.'
              }
            ].map(p=>(
              <div key={p.t} className="bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/30 transition-all text-center">
                <div className="text-4xl mb-4">{p.ico}</div>
                <h3 className="text-white font-bold text-lg mb-2">{p.t}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet The Team */}
      <MeetTheTeamSection />

      {/* Values */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="eyebrow">Values</p>
          <h2 className="section-heading mb-10">What We Stand For</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[{ico:'🎯',t:'Precision',d:'Accuracy is non-negotiable. Every order, every inventory count, every delivery, done right.'},{ico:'🌿',t:'Partnership',d:'We grow when you grow. Your logistics challenges are our challenges.'},{ico:'🔍',t:'Transparency',d:'Clear pricing, real-time data, honest communication. No hidden fees.'},{ico:'🚀',t:'Innovation',d:'We continuously improve our platform and processes to stay ahead.'}].map(v=>(
              <div key={v.t} className="bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/30 transition-all">
                <div className="text-3xl mb-3">{v.ico}</div>
                <h3 className="text-white font-bold text-base mb-2">{v.t}</h3>
                <p className="text-white/50 text-sm">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTAStrip headline="Join the THL Community" sub="Hundreds of businesses have chosen THL as their logistics partner." btn1="Get in Touch →" btn2="Visit Our Facilities" btn2Path="/facilities" />
      <Footer />
    </div>
  );
}
